import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Settings,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  CheckCircle,
  User,
  CreditCard,
  Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.passwordsNoMatch'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('errors.passwordLength'));
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await fetch(`${API}/owner/settings/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        setPasswordChanged(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        toast.success(t('settings.passwordChanged'));
        setTimeout(() => setPasswordChanged(false), 3000);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB]">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/owner')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              {t('settings.title')}
            </h1>
          </div>
        </div>

        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-soft p-6 mb-6"
        >
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            {t('settings.accountInfo')}
          </h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">{t('settings.email')}</span>
              <span className="font-medium text-slate-900">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-slate-100">
              <span className="text-slate-600">{t('settings.name')}</span>
              <span className="font-medium text-slate-900">
                {user?.first_name} {user?.last_name}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600">{t('settings.plan')}</span>
              <span className={`font-bold px-3 py-1 rounded-full text-sm ${
                user?.account_type === 'business' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'bg-[#0A2540]/10 text-[#0A2540]'
              }`}>
                {user?.account_type?.toUpperCase() || 'PRO'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Change Password */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-soft p-6"
        >
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {t('settings.changePassword')}
          </h2>
          
          {passwordChanged ? (
            <div className="bg-[#00C48C]/5 border border-[#00C48C]/20 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#00C48C]" />
              <p className="text-[#00A67E] font-medium">{t('settings.passwordChanged')}</p>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">{t('settings.currentPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="currentPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="pl-10"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('settings.newPassword')}</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="newPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('settings.confirmPassword')}</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={changingPassword}
                className="w-full btn-primary rounded-full"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('settings.updatePassword')
                )}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
